/**
 * useArtifacts
 * Owns the conversation's live artifact state and reduces the artifact_* SSE
 * events into it. Opening the panel on the first artifact_start is what gives
 * the "agent announces + panel opens at the same moment" behaviour.
 */

import { useCallback, useRef, useState } from 'react';
import { createLogger } from '@/utils/logger';
import artifactService from '../services/artifactService';
import type { Artifact, OutputFormat } from '@/types/models';

const logger = createLogger('useArtifacts');

interface ArtifactEvent {
  type: string;
  tool?: string;
  format?: OutputFormat;
  title?: string;
  artifact_file_id?: string;
  artifact?: {
    artifact_file_id?: string;
    version?: number;
    format?: OutputFormat;
    title?: string;
    status?: string;
  };
}

// Resize bounds — mirror the AoA inspector's clamp behaviour.

export interface UseArtifactsResult {
  artifacts: Record<string, Artifact>;
  order: string[];
  activeId: string | null;
  panelOpen: boolean;
  setActiveId: (id: string) => void;
  openPanel: () => void;
  closePanel: () => void;
  openArtifact: (fileId: string) => Promise<void>;
  viewVersion: (fileId: string, version: number) => Promise<void>;
  handleEvent: (event: ArtifactEvent) => void;
  loadArtifact: (fileId: string) => Promise<void>;
  removeArtifact: (fileId: string) => Promise<void>;
  deleteVersion: (fileId: string, version: number) => Promise<void>;
  hydrateFromMessages: (messages: any[], conversationId?: string | null) => Promise<void>;
  reconcileGenerating: () => Promise<void>;
  reset: () => void;
}

export function useArtifacts(): UseArtifactsResult {
  const [artifacts, setArtifacts] = useState<Record<string, Artifact>>({});
  // Always-fresh mirror of `artifacts` so callbacks invoked from long-lived
  // stream handlers (which close over an older render) still see current state.
  const artifactsRef = useRef<Record<string, Artifact>>({});
  artifactsRef.current = artifacts;
  const [order, setOrder] = useState<string[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const activeIdRef = useRef<string | null>(null);
  activeIdRef.current = activeId;
  // Bumped by reset() — a conversation switch. A read still in flight for the
  // previous conversation lands after it; without this it put that
  // conversation's files, and its newest one ON SCREEN, into the next one
  // (item 59: a new meta-agent session opened on "Files 2" of the last).
  const generationRef = useRef(0);
  const [panelOpen, setPanelOpen] = useState(false);
  const upsert = useCallback((id: string, patch: Partial<Artifact>) => {
    setArtifacts(prev => {
      const base: Artifact = prev[id] || { artifact_id: id, status: 'streaming' };
      return { ...prev, [id]: { ...base, ...patch, artifact_id: id } };
    });
    setOrder(prev => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const loadArtifact = useCallback(async (fileId: string) => {
    const generation = generationRef.current;
    try {
      const detail = await artifactService.getDetail(fileId);
      if (generation !== generationRef.current) return;  // the conversation changed meanwhile
      upsert(fileId, {
        title: detail.title,
        output_format: detail.format,
        status: detail.format === 'gforms' && detail.google_form?.status !== 'created'
          ? 'awaiting_confirm'
          : 'ready',
        current_version: detail.version,
        branded: detail.branded,
        brand_source: detail.brand_source,
        draft_conversation_id: detail.draft_conversation_id,
        doc_model: detail.doc_model,
        formula_values: detail.formula_values,
        versions: detail.revision_history,
        drive_copies: detail.drive_copies,
        can_edit: detail.can_edit,
        form_url: detail.google_form?.responder_uri,
        form_edit_url: detail.google_form?.edit_uri,
        form_status: detail.google_form?.status as any,
      });
    } catch (err) {
      logger.error('Failed to load artifact detail', err);
    }
  }, [upsert]);

  const handleEvent = useCallback((event: ArtifactEvent) => {
    if (event.type === 'artifact_start') {
      // Open the panel immediately — paired with the agent's chat announcement.
      setPanelOpen(true);
      const op: 'create' | 'edit' = event.tool === 'edit_artifact' ? 'edit' : 'create';
      const id = event.artifact_file_id;
      if (id) {
        upsert(id, {
          title: event.title,
          output_format: event.format,
          status: 'streaming',
          op,
        });
        setActiveId(id);
      }
      // (A create with no file id yet simply opens the panel; the matching
      // artifact_render carries the id and full metadata.)
    } else if (event.type === 'artifact_render') {
      const a = event.artifact || {};
      const id = a.artifact_file_id;
      if (!id) return;
      const isForm = a.format === 'gforms';
      upsert(id, {
        title: a.title,
        output_format: a.format,
        current_version: a.version,
        status: isForm && a.status !== 'created' ? 'awaiting_confirm' : 'ready',
      });
      setActiveId(id);
      setPanelOpen(true);
      // Pull the full model + history for the preview.
      void loadArtifact(id);
    } else if (event.type === 'artifact_show') {
      // Nothing was generated: make the existing artifact active and open the
      // panel — the same motion as the user clicking its card in the chat.
      const id = event.artifact?.artifact_file_id;
      if (!id) return;
      setActiveId(id);
      setPanelOpen(true);
      if (!artifacts[id]?.doc_model) void loadArtifact(id);
    } else if (event.type === 'artifact_error') {
      const id = event.artifact_file_id;
      if (id) upsert(id, { status: 'error' });
    }
  }, [upsert, loadArtifact, artifacts]);

  // Open the panel to a specific artifact (used by clicking an attachment in
  // the chat). Ensures it's loaded, makes it active, expands the panel.
  const openArtifact = useCallback(async (fileId: string) => {
    setActiveId(fileId);
    setPanelOpen(true);
    if (!artifacts[fileId]?.doc_model) {
      await loadArtifact(fileId);
    }
  }, [artifacts, loadArtifact]);

  // View a specific version (read-only). Selecting the current version clears
  // the viewing override and shows the live model again.
  const viewVersion = useCallback(async (fileId: string, version: number) => {
    const current = artifacts[fileId]?.current_version;
    if (version === current) {
      upsert(fileId, { viewing: null });
      return;
    }
    try {
      const v = await artifactService.getVersion(fileId, version);
      upsert(fileId, {
        viewing: { version: v.version, doc_model: v.doc_model, formula_values: v.formula_values },
      });
    } catch (err) {
      logger.error('Failed to load artifact version', err);
    }
  }, [artifacts, upsert]);

  // Drop an artifact from panel state (after it's been deleted server-side). If
  // it was active, switch to another (or close the panel when none remain).
  const dropFromState = useCallback((fileId: string) => {
    setArtifacts(prev => {
      const next = { ...prev };
      delete next[fileId];
      return next;
    });
    setOrder(prev => {
      const next = prev.filter(id => id !== fileId);
      setActiveId(cur => (cur === fileId ? (next[next.length - 1] ?? null) : cur));
      if (next.length === 0) setPanelOpen(false);
      return next;
    });
  }, []);

  // Permanently delete a whole artifact.
  const removeArtifact = useCallback(async (fileId: string) => {
    await artifactService.remove(fileId);
    dropFromState(fileId);
  }, [dropFromState]);

  // Delete a single version. If it was the artifact's last version the whole
  // artifact is removed; otherwise refresh its detail (new current + versions).
  const deleteVersion = useCallback(async (fileId: string, version: number) => {
    const res = await artifactService.removeVersion(fileId, version);
    if (res.artifact_deleted) {
      dropFromState(fileId);
      return;
    }
    // Clear any viewing override (it may point at the deleted version), then reload.
    upsert(fileId, { viewing: null });
    await loadArtifact(fileId);
  }, [dropFromState, loadArtifact, upsert]);

  // Safety net: when a turn ends, never leave an artifact stuck in the
  // "generating" state. This happens if artifact_start fired but no
  // artifact_render followed — e.g. the agent asked a question instead of
  // finishing, the edit errored, or the stream was aborted. Re-fetch each
  // still-generating artifact: if it exists, settle it to its last good state;
  // if it was never persisted, drop it (and close the panel if empty).
  const reconcileGenerating = useCallback(async () => {
    const stuck = Object.entries(artifactsRef.current)
      .filter(([, a]) => a.status === 'streaming' || a.status === 'rendering')
      .map(([id]) => id);
    const generation = generationRef.current;
    await Promise.all(stuck.map(async (id) => {
      try {
        const detail = await artifactService.getDetail(id);
        if (generation !== generationRef.current) return;  // the conversation changed meanwhile
        upsert(id, {
          title: detail.title,
          output_format: detail.format,
          status: detail.format === 'gforms' && detail.google_form?.status !== 'created'
            ? 'awaiting_confirm' : 'ready',
          current_version: detail.version,
          doc_model: detail.doc_model,
          formula_values: detail.formula_values,
          versions: detail.revision_history,
          drive_copies: detail.drive_copies,
          can_edit: detail.can_edit,
          form_url: detail.google_form?.responder_uri,
          form_edit_url: detail.google_form?.edit_uri,
          form_status: detail.google_form?.status as any,
        });
      } catch {
        if (generation === generationRef.current) dropFromState(id);  // never persisted → remove the stuck card
      }
    }));
  }, [upsert, dropFromState]);

  // On opening an existing conversation, pull in every artifact it produced
  // and open the panel to the latest — so the user sees it immediately
  // without having to click an artifact in the chat.
  //
  // Message attachments are the fast path, but they are NOT the source of
  // truth: they are written by a turn-end bind, and an artifact whose turn
  // ended by a route that skipped that bind had no attachment at all. Since
  // the whole dock — panel, tabs, even the collapsed rail — only exists once
  // an artifact is loaded, such a file became completely unreachable from the
  // chat that made it. So ask the conversation as well, which answers from
  // the artifacts' own records, and union the two.
  const hydrateFromMessages = useCallback(
    async (messages: any[], conversationId?: string | null) => {
      const generation = generationRef.current;
      const activeAtStart = activeIdRef.current;
      const ids: string[] = [];
      for (const m of messages || []) {
        for (const att of (m?.attachments || [])) {
          const aid = att?.artifact_id || (att?.is_agent_generated ? att?.file_id : null);
          if (aid && !ids.includes(aid)) ids.push(aid);
        }
      }
      if (conversationId) {
        try {
          // oldest-first, so the newest still ends up last and becomes active
          const listed = (await artifactService.listForConversation(conversationId))
            .slice().reverse();
          for (const a of listed) {
            const aid = a?.artifact_file_id;
            if (aid && !ids.includes(aid)) ids.push(aid);
          }
        } catch {
          /* attachments alone still work — never block opening the chat */
        }
      }
      if (ids.length === 0 || generation !== generationRef.current) return;
      await Promise.all(ids.map(id => loadArtifact(id)));
      if (generation !== generationRef.current) return;
      // The newest stored file becomes the subject — unless a file was put on
      // screen while these reads ran (one the agent just wrote, or a click):
      // that one is newer than anything this list knew about.
      if (activeIdRef.current === activeAtStart) {
        setActiveId(ids[ids.length - 1]);
      }
      setPanelOpen(true);
    }, [loadArtifact]);

  const reset = useCallback(() => {
    generationRef.current += 1;
    // Now, not at the next render: ConversationPanel calls hydrateFromMessages
    // right after reset(), and the restore compares against this.
    activeIdRef.current = null;
    setArtifacts({});
    setOrder([]);
    setActiveId(null);
    setPanelOpen(false);
  }, []);

  return {
    artifacts,
    order,
    activeId,
    panelOpen,
    setActiveId,
    openPanel: () => setPanelOpen(true),
    closePanel: () => setPanelOpen(false),
    openArtifact,
    viewVersion,
    handleEvent,
    loadArtifact,
    removeArtifact,
    deleteVersion,
    hydrateFromMessages,
    reconcileGenerating,
    reset,
  };
}

export default useArtifacts;
